import React, { useContext, useEffect, useMemo, useState } from 'react';
import QuestionSharing from '../questionSharing/QuestionSharing';

import { CalculatorsContext, DraftContext } from '../../context';
import { addShare, getShareList } from '../../api/rest/list';
import { openNotification } from 'tt-ui-lib/core';
import { CALC_MODELS } from '../../constants';

const QuestionSharingWrapper = (props) => {
  const { categoryCode, questionId, model } = props;
  const { customCo2Answers, carbonQuestions, config, calcSteps } = useContext(CalculatorsContext);
  const { draft, user } = useContext(DraftContext);
  const [treeData, setTreeData] = useState(null);
  const [selectedKeys, setSelectedKeys] = useState([]);
  const [invitedUsers, setInvitedUsers] = useState(null);

  const questionTree = useMemo(() => {
    if (!carbonQuestions) return [];

    const customCo2Scopes = (calcSteps || [])
      .filter((step) => step.model === CALC_MODELS.CUSTOM_CO2)
      .reduce((acc, step) => {
        acc[step.scopeIndx] = true;
        return acc;
      }, {});

    return carbonQuestions.map((scope, scopeIndx) => {
      const categoryNodes = scope.categories.map((category) => ({
        title: category.name,
        key: category.code,
        children: (category.questions || []).map((question) => ({
          title: question.name,
          key: `${category.code}|${question.id}`,
          id: question.id,
          catCode: category.code,
          scopeCode: scope.code,
        })),
      }));

      const children = [];

      if (customCo2Scopes[scopeIndx]) {
        children.push({
          title: 'Custom CO2',
          key: `${CALC_MODELS.CUSTOM_CO2}_${scopeIndx}`,
          isCustom: true,
          scopeIndex: scopeIndx,
        });
      }

      children.push(...categoryNodes);

      return {
        title: scope.name,
        key: scope.code,
        children,
      };
    });
  }, [carbonQuestions, calcSteps]);

  useEffect(() => setTreeData(questionTree ?? []), [questionTree]);

  useEffect(() => {
    if (!treeData || !categoryCode || !questionId) return;
    const selectedKey = `${categoryCode}|${questionId}`;
    const selected = treeData.reduce((acc, scope) => {
      const filtered = scope.children.filter((child) => child.key === categoryCode);
      if (filtered.length) {
        if (model === CALC_MODELS.CUSTOM_CO2) {
          acc.push(filtered[0].key, scope.key);
        } else {
          acc.push(selectedKey, filtered[0].key, scope.key);
        }
      }
      return acc;
    }, []);
    setSelectedKeys([...selected]);
  }, [treeData, categoryCode, questionId]);

  const onResultCallback = ({ addressee, data }) => {
    if (!addressee || !data) return;
    const sender = {
      first_name: user.firstName,
      last_name: user.lastName,
      company_name: draft.companyName,
      tool_name: config.name,
    };
    const payload = {
      ...addressee,
      sender,
      calculator_id: draft.id,
      calculator_type: 'carbon',
      receivers: [{ id: user.id, type: 'USER' }],
      questions: data.map((el) => {
        if (el.isCustom) {
          return {
            outer_id: el.scopeIndex.toString(),
            type: CALC_MODELS.CUSTOM_CO2,
            name: el.title,
            data: {
              scope_code: el.scopeIndex,
              scope_items: Object.values(customCo2Answers.answers[el.scopeIndex] ?? {}).flat(),
            },
          };
        }

        return {
          outer_id: el.id,
          type: 'carbon',
          name: el.title,
          data: {
            category_code: el.catCode,
            scope_code: el.scopeCode,
          },
        };
      }),
    };
    addShare(payload)
      .then(() => {
        openNotification({
          message: 'Your invitation has been successfully sent',
          type: 'success',
        });
        getShareList({ calculator_id: draft.id }).then((res2) => setInvitedUsers(res2.data.data));
      })
      .catch((e) => {
        openNotification({
          message: "There's been an error. Try again later",
          type: 'error',
        });
      });
  };

  useEffect(() => {
    getShareList({ calculator_id: draft.id }).then((res) => setInvitedUsers(res.data.data));
  }, []);

  return (
    <QuestionSharing
      treeData={treeData}
      selectedInit={selectedKeys}
      resultCallback={onResultCallback}
      invitedUsers={invitedUsers}
    />
  );
};

export default QuestionSharingWrapper;
